// src/components/inventory/POJustificationModal.js
// --- UPDATE (Procurement MOW+ v1) ----------------------------------------------------------
//   • FlowSense facts beside the memo: stock, usage per day, days of cover, re-order point, lead
//     time and what is already on order (inventory ML insight for this item).
//   • Works when the AI engine is offline: FlowSense writes the memo from those real numbers.
//   • Copy button; the metrics badges show "—" when unknown instead of "undefined".
//   • Fix: MdBrain is not an icon in react-icons/md (the import resolved to undefined).
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useEffect } from 'react'; // --- UPDATE: + useEffect
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, Textarea, VStack, Text, HStack, Icon, Badge, Spinner, useToast, Box
} from '@chakra-ui/react';
import { MdLightbulbOutline, MdAttachMoney, MdShowChart, MdAutoAwesome, MdContentCopy } from 'react-icons/md'; // --- UPDATE: MdBrain does not exist
import { FaRobot } from 'react-icons/fa';
import aiService from '../../services/aiService';
// --- UPDATE (Procurement MOW+ v1)
import { SimpleGrid, useColorModeValue } from '@chakra-ui/react';
import inventoryIntelApi from '../../services/inventoryIntelApi';
import { localJustification, usd } from './procurementAiIntel';
// --- END UPDATE

const POJustificationModal = ({ isOpen, onClose, item, orderQuantity, onSaveJustification, supplierName = null, unitPrice = null, budgetCode = null }) => { // --- UPDATE: + supplierName, unitPrice, budgetCode
  const [justification, setJustification] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const toast = useToast();
  // --- UPDATE (Procurement MOW+ v1): FlowSense insight for the item
  const [insight, setInsight] = useState(null);
  const [source, setSource] = useState(null);
  const factBg = useColorModeValue('teal.50', 'whiteAlpha.100');
  const muted = useColorModeValue('gray.600', 'gray.400');
  useEffect(() => {
    if (!isOpen || !item?.id) return undefined;
    let alive = true;
    setJustification(''); setMetrics(null); setSource(null);
    inventoryIntelApi.insight(item.id).then((r) => { if (alive) setInsight(r?.item || null); }).catch(() => { if (alive) setInsight(null); });
    return () => { alive = false; };
  }, [isOpen, item]);
  const price = unitPrice ?? insight?.unit_price ?? item?.unit_price ?? null;
  // --- END UPDATE

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await aiService.generatePOJustification(item.id, orderQuantity);
      if (response.success) {
        setJustification(response.draft);
        setMetrics(response.metrics);
        setSource('ai'); // --- UPDATE
        toast({ title: 'AI Justification Drafted', status: 'success', duration: 2000 });
      }
    } catch (error) {
      // --- UPDATE: AI engine offline → FlowSense memo from real numbers
      const memo = localJustification({ items: [{ name: item?.name, quantity: orderQuantity, unit_price: price, insight }], supplierName, budgetCode, total: price !== null ? price * Number(orderQuantity || 0) : null });
      if (memo) {
        setJustification(memo); setSource('flowsense');
        setMetrics({ totalCost: price !== null ? (price * Number(orderQuantity || 0)).toFixed(2) : null, recentUsage: insight?.usage_30d ?? null, predictedNeed: insight?.demand_30d ?? null });
        toast({ title: 'FlowSense memo drafted', description: 'The AI engine is offline — built from inventory records.', status: 'info', duration: 3500 });
      } else
      // --- END UPDATE
      toast({ title: 'Generation Failed', description: 'Ensure the local AI engine is running.', status: 'error', duration: 4000 });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl">
        <Box h="3px" bgGradient="linear(to-r, purple.400, blue.500)" borderTopRadius="xl" />
        <ModalHeader>
          <HStack>
            <Icon as={FaRobot} color="purple.500" />
            <Text>AI Budget Justification</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color={muted}>{/* --- UPDATE: dark mode */}
              Generate a data-backed memo for the Ministry of Works Finance Committee to approve <b>{orderQuantity}x {item?.name}</b>.
            </Text>
            {/* --- UPDATE: FlowSense facts */}
            {insight && (
              <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2} p={3} bg={factBg} borderRadius="lg" fontSize="xs" role="note" aria-label="FlowSense facts">
                <Box><Text color={muted}>In stock</Text><Text fontWeight="bold">{insight.quantity}</Text></Box>
                <Box><Text color={muted}>Usage / day (90 d)</Text><Text fontWeight="bold">{insight.avg_daily_usage ?? '—'}</Text></Box>
                <Box><Text color={muted}>Days of cover</Text><Text fontWeight="bold">{insight.days_of_cover ?? '—'}</Text></Box>
                <Box><Text color={muted}>Re-order point</Text><Text fontWeight="bold">{insight.effective_reorder_point ?? '—'}</Text></Box>
                <Box><Text color={muted}>Lead time</Text><Text fontWeight="bold">{insight.lead_time_days ?? '—'} d</Text></Box>
                <Box><Text color={muted}>Already on order</Text><Text fontWeight="bold">{insight.on_order ?? 0}</Text></Box>
              </SimpleGrid>
            )}

            {!justification && !isGenerating && (
              <Button 
                colorScheme="purple" 
                leftIcon={<MdLightbulbOutline />} 
                onClick={handleGenerate}
                h="100px"
                variant="outline"
                borderStyle="dashed"
                borderWidth="2px"
              >
                Draft Justification Memo
              </Button>
            )}

            {isGenerating && (
              <VStack py={8} spacing={4}>
                <Spinner color="purple.500" size="xl" thickness="3px" />
                <Text color="purple.600" fontWeight="medium" css={{ animation: 'pulse 2s infinite' }}>
                  Analyzing historical usage & predictive forecasts...
                </Text>
              </VStack>
            )}

            {justification && !isGenerating && (
              <Box>
                {metrics && (
                  <HStack spacing={3} mb={4} p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.100">
                    <Badge colorScheme="green"><Icon as={MdAttachMoney} /> Est Cost: {metrics.totalCost != null ? usd(metrics.totalCost) : '—'}</Badge>{/* --- UPDATE: "—" when unknown */}
                    <Badge colorScheme="blue"><Icon as={MdShowChart} /> 30-Day Usage: {metrics.recentUsage ?? '—'}</Badge>
                    <Badge colorScheme="purple"><Icon as={MdAutoAwesome} /> {source === 'flowsense' ? 'FlowSense 30-day demand' : 'AI Predicted Need'}: {metrics.predictedNeed ?? '—'}</Badge>
                  </HStack>
                )}
                <Textarea 
                  value={justification} 
                  onChange={(e) => setJustification(e.target.value)} 
                  rows={8} 
                  borderRadius="md"
                  focusBorderColor="purple.400"
                />
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          {/* --- UPDATE: copy */}
          {justification && <Button variant="ghost" mr="auto" leftIcon={<Icon as={MdContentCopy} />} onClick={async () => { try { await navigator.clipboard.writeText(justification); toast({ title: 'Copied', status: 'success', duration: 1500 }); } catch (_) { toast({ title: 'Select the text to copy it', status: 'info' }); } }}>Copy</Button>}
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button 
            colorScheme="blue" 
            isDisabled={!justification}
            onClick={() => {
              onSaveJustification(justification);
              onClose();
            }}
          >
            Use this Justification
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default POJustificationModal;